import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { View, StyleSheet } from 'react-native';

import { theme } from '@garnish/constants';

import { useA11ySelector } from '@sg/a11y-helpers';
import { HStack, Button, BodyText } from '@sg/garnish';
import type { Product } from '@sg/graphql-schema';

import { useTrackEffect } from '@order/Telemetry';

import { BagUpsellProductOption } from '../BagUpsellProductOption';

export const BagUpsellProductGrid = (props: BagUpsellProductGridProps) => {
  const {
    upsellIdsBeingAdded,
    upsellProducts,
    addUpsellToBag,
    startChangingLineItemQuantity,
    stopChangingLineItemQuantity,
    isAddingUpsellToBag = false,
    isContinueDisabled = false,
    isDisabled = false,
    onContinue,
  } = props;

  const a11ySelector = useA11ySelector('bag.upsell-products.container');

  // ─── Stepper Interactions ──────────────────────────────────────────────────

  const updateOpenSteppers = useCallback(
    (productId: string, isStepperShown: boolean) => {
      if (isStepperShown) {
        return startChangingLineItemQuantity(productId);
      }

      stopChangingLineItemQuantity(productId);
    },
    [startChangingLineItemQuantity, stopChangingLineItemQuantity],
  );

  // ─── Telemetry ───────────────────────────────────────────────────────────────────

  useTrackEffect('bag_upsell.grid.view');

  // ─── Flags ───────────────────────────────────────────────────────────────────

  const areSteppersActive = upsellIdsBeingAdded.length > 0;

  // ──────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container} {...a11ySelector.props}>
      <BodyText sizeMatch={['24']} bold>
        <FormattedMessage {...messages.header} />
      </BodyText>

      <View style={styles.contentContainer}>
        <HStack gap={theme.spacing['2']} style={styles.grid}>
          {upsellProducts.map((product) => {
            const isActive = upsellIdsBeingAdded.includes(product.id);

            return (
              <BagUpsellProductOption
                key={product.id}
                product={product}
                size="lg"
                addUpsellToBag={addUpsellToBag}
                isAddingToCart={isAddingUpsellToBag && isActive}
                isDisabled={isDisabled || (areSteppersActive && !isActive)}
                updateOpenSteppers={updateOpenSteppers}
              />
            );
          })}
        </HStack>

        <Button
          onPress={onContinue}
          size="large-wide"
          disabled={isContinueDisabled || areSteppersActive}
        >
          <FormattedMessage {...messages.continueCta} />
        </Button>
      </View>
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  header: {
    defaultMessage: 'Complete your order',
    description: 'Bag | Upsell | Modal | Header',
  },
  continueCta: {
    defaultMessage: 'Continue',
    description: 'Bag | Upsell | Modal | Continue CTA Label',
  },
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.WHITE,
    padding: theme.spacing['6'],
    gap: theme.spacing['6'],
  },
  contentContainer: {
    gap: theme.spacing['10'],
  },
  grid: {
    rowGap: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BagUpsellProductGridProps = {
  upsellProducts: UpsellProduct[];
  upsellIdsBeingAdded: string[];
  addUpsellToBag: (payload: { quantity: number; product: Partial<Product> }) => void;
  startChangingLineItemQuantity: (lineItemId: string) => void;
  stopChangingLineItemQuantity: (lineItemId: string) => void;
  isAddingUpsellToBag?: boolean;
  isContinueDisabled?: boolean;
  isDisabled?: boolean;
  onContinue: () => void;
};

type UpsellProduct = Readonly<{
  id: string;
  name: string;
  cost: number;
  imageUrl: string;
  outOfStock: boolean;
  menuId: number;
}>;
